/**
 * API Service
 * 
 * Axios-based HTTP client for backend API communication.
 * Implements Constitution Principle V (Auditability) with correlation ID injection.
 * 
 * Task: T051
 * 
 * Features:
 * - Base URL configuration from environment variables
 * - Automatic correlation ID generation and injection
 * - Error response interceptor with user-friendly messages
 * - Request/response logging (development mode)
 * - Timeout configuration
 */

import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// Base URL from environment variable
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/v1';
const ENABLE_DEBUG_LOGGING = import.meta.env.VITE_ENABLE_DEBUG_LOGGING === 'true';

// Create Axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: Add correlation ID to all requests
apiClient.interceptors.request.use(
  (config) => {
    // Generate or use existing correlation ID
    const correlationId = config.headers['X-Correlation-ID'] || uuidv4();
    config.headers['X-Correlation-ID'] = correlationId;
    
    if (ENABLE_DEBUG_LOGGING) {
      console.log('[API Request]', {
        method: config.method?.toUpperCase(),
        url: config.url,
        correlationId,
        data: config.data,
      });
    }
    
    return config;
  },
  (error) => {
    if (ENABLE_DEBUG_LOGGING) {
      console.error('[API Request Error]', error);
    }
    return Promise.reject(error);
  }
);

// Response interceptor: Log responses and handle errors
apiClient.interceptors.response.use(
  (response) => {
    if (ENABLE_DEBUG_LOGGING) {
      console.log('[API Response]', {
        status: response.status,
        correlationId: response.headers['x-correlation-id'],
        data: response.data,
      });
    }
    return response;
  },
  (error) => {
    if (ENABLE_DEBUG_LOGGING) {
      console.error('[API Response Error]', {
        status: error.response?.status,
        correlationId: error.response?.headers?.['x-correlation-id'],
        message: error.response?.data?.message || error.message,
      });
    }
    
    // Enhance error with user-friendly message
    if (error.response) {
      // Server responded with error status
      error.userMessage = error.response.data?.message || 'An error occurred while processing your request';
      error.correlationId = error.response.headers?.['x-correlation-id'];
    } else if (error.request) {
      // Request made but no response received
      error.userMessage = 'Unable to connect to the payment server. Please check your internet connection.';
    } else {
      // Error setting up the request
      error.userMessage = 'An unexpected error occurred. Please try again.';
    }
    
    return Promise.reject(error);
  }
);

/**
 * Validate payment request credentials
 * 
 * @param {Object} credentials - Payment request credentials
 * @param {string} credentials.email - Customer email address
 * @param {string} credentials.requestId - Payment request ID (GUID)
 * @returns {Promise<Object>} Validation result
 * @throws {Error} If validation fails
 */
export async function validatePaymentRequest({ email, requestId }) {
  const response = await apiClient.post('/payment-requests/validate', {
    email,
    requestId,
  });
  
  return response.data;
}

/**
 * Get Braintree client token for payment request
 * 
 * @param {string} requestId - Payment request ID (GUID)
 * @returns {Promise<Object>} Client token data
 * @returns {string} clientToken - Base64-encoded Braintree client token
 * @returns {string} expiresAt - ISO 8601 timestamp when token expires
 * @throws {Error} If token generation fails
 */
export async function getClientToken(requestId) {
  const response = await apiClient.get('/braintree/client-token', {
    params: { requestId },
  });
  
  return response.data;
}

/**
 * Submit payment method with Braintree nonce
 * 
 * @param {Object} paymentData - Payment method data
 * @param {string} paymentData.requestId - Payment request ID (GUID)
 * @param {string} paymentData.nonce - Braintree payment method nonce
 * @returns {Promise<Object>} Payment method creation result
 * @returns {number} paymentMethodId - ID of created payment method
 * @returns {string} requestId - Payment request ID
 * @returns {string} message - Success message
 * @throws {Error} If payment submission fails
 */
export async function submitPaymentMethod({ requestId, nonce }) {
  const response = await apiClient.post('/payment-methods', {
    requestId,
    nonce,
  });
  
  return response.data;
}

/**
 * Resolve a Salesforce case payment request
 *
 * Finds an existing request for the case or validates the case live and creates one.
 *
 * @param {Object} params - Resolve parameters
 * @param {string} params.email - Customer email address
 * @param {string} params.caseId - Salesforce Case Id
 * @returns {Promise<Object>} { requestId, email, caseNumber, status, type }
 * @throws {Error} If resolution fails
 */
export async function resolveSalesforceRequest({ email, caseId }) {
  const response = await apiClient.post('/salesforce-requests/resolve', {
    email,
    caseId,
  });

  return response.data;
}

/**
 * Get API health status
 * 
 * @returns {Promise<Object>} Health status
 */
export async function getHealthStatus() {
  // Health endpoint is mounted at the server root, not under the /v1 API prefix.
  const response = await apiClient.get('/health', { baseURL: API_BASE_URL.replace(/\/v1\/?$/, '') });
  return response.data;
}

/**
 * Extract user-friendly error message from API error
 * 
 * @param {Error} error - Axios error
 * @returns {string} User-friendly error message
 */
export function getErrorMessage(error) {
  return error.userMessage || error.message || 'An unexpected error occurred';
}

/**
 * Extract correlation ID from API error
 * 
 * @param {Error} error - Axios error
 * @returns {string|null} Correlation ID if available
 */
export function getCorrelationId(error) {
  return error.correlationId || null;
}

export default apiClient;
